import { create } from 'zustand';
import { useCallback } from 'react';

interface VideoPlayerState {
  // Playback state - core video functionality
  position: number;
  duration: number;
  isPlaying: boolean;
  isBuffering: boolean;
  isFullscreen: boolean;

  // Resource context - what's playing
  resourceId: string | null;

  // Persistence tracking
  isDirty: boolean;
  lastPersistedAt: number;

  // Actions
  setPlaybackPosition: (position: number, duration: number) => void;
  setPlaybackStatus: (isPlaying: boolean, isBuffering?: boolean) => void;
  setFullscreenStatus: (isFullscreen: boolean) => void;
  setResource: (resourceId: string) => void;
  markDirty: () => void;
  markClean: () => void;
  resetState: () => void;
}

// Create the store with minimal implementation
export const useVideoPlayerStore = create<VideoPlayerState>((set) => ({
  // Initial state
  position: 0,
  duration: 0,
  isPlaying: false,
  isBuffering: false,
  resourceId: null,
  isDirty: false,
  lastPersistedAt: 0,
  isFullscreen: false,
  // Actions with focused updates
  setPlaybackPosition: (position, duration) => set((state) => ({
    position,
    duration,
    // Mark as dirty only when significant progress has been made (>3 seconds)
    isDirty: state.isDirty || Math.abs(position - state.position) > 3000,
  })),

  setPlaybackStatus: (isPlaying, isBuffering = false) => set((state) => ({
    isPlaying,
    isBuffering,
    // Always mark as dirty on play/pause transitions
    isDirty: state.isDirty || state.isPlaying !== isPlaying,
  })),

  setFullscreenStatus: (isFullscreen) => set({
    isFullscreen,
  }),

  setResource: (resourceId) => set({ resourceId, isDirty: false }),

  markDirty: () => set({ isDirty: true }),

  markClean: () => set({
    isDirty: false,
    lastPersistedAt: Date.now(),
  }),

  resetState: () => set({
    position: 0,
    duration: 0,
    isPlaying: false,
    isBuffering: false,
    isDirty: false,
  }),
}));

// Focused hook for just playback status - used by controls and UI elements
export function useVideoPlaybackStatus() {
  const isPlaying = useVideoPlayerStore((state) => state.isPlaying);
  const isBuffering = useVideoPlayerStore((state) => state.isBuffering);
  const isFullscreen = useVideoPlayerStore((state) => state.isFullscreen);
  const setPlaybackStatus = useVideoPlayerStore((state) => state.setPlaybackStatus);

  return {
    isPlaying,
    isBuffering,
    isFullscreen,
    setPlaybackStatus,
  };
}

// Focused hook for progress tracking - used by progress bar and transcript
export function useVideoProgress() {
  const position = useVideoPlayerStore((state) => state.position);
  const duration = useVideoPlayerStore((state) => state.duration);
  const setPlaybackPosition = useVideoPlayerStore((state) => state.setPlaybackPosition);

  return {
    position,
    duration,
    setPlaybackPosition,
  };
}

// Hook for persistence operations - used by VideoPlayerWithProgress
export function useVideoPersistence() {
  const {
    isDirty,
    position,
    duration,
    resourceId,
    markClean,
  } = useVideoPlayerStore((state) => ({
    isDirty: state.isDirty,
    position: state.position,
    duration: state.duration,
    resourceId: state.resourceId,
    markClean: state.markClean,
  }));

  // Convert milliseconds to seconds for API
  const getProgressData = useCallback(() => {
    if (!position || !duration) return null;

    const watchedSeconds = Math.floor(position / 1000);
    const totalSeconds = Math.floor(duration / 1000);
    const completed = watchedSeconds > (totalSeconds - 10); // Consider complete if within 10 seconds of end

    return {
      completed,
      progress: {
        watchedSeconds,
        totalSeconds,
      },
    };
  }, [position, duration]);

  return {
    isDirty,
    resourceId,
    getProgressData,
    markClean,
  };
}

// Hook for resource initialization - used when mounting the player
export function useVideoResourceInit() {
  const setResource = useVideoPlayerStore((state) => state.setResource);
  const resetState = useVideoPlayerStore((state) => state.resetState);

  const initializeResource = useCallback((resourceId: string) => {
    resetState();
    setResource(resourceId);
  }, [setResource, resetState]);

  return { initializeResource };
}
